import React from "react";
import { Providers } from "@tripian/model";
import {
  isLivnNumericQuestion,
  isLivnResourceQuestion,
  livnAnswerHint,
  parseLivnNumericBounds,
} from "../../../utils/livnValidation";

interface ILivnQuestionInput {
  question: Providers.Livn.Question;
  value: string;
  error?: string;
  onChange: (value: string) => void;
  onBlur: () => void;
}

const INPUT_CLS =
  "w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-color";

const LivnQuestionInput: React.FC<ILivnQuestionInput> = ({ question: q, value, error, onChange, onBlur }) => {
  const cls = `${INPUT_CLS} ${error ? "border-red-400 bg-red-50/40" : "border-gray-300"}`;
  const hint = livnAnswerHint(q);

  const wrap = (input: React.ReactNode) => (
    <div className="flex flex-col gap-1">
      {input}
      {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
      {error && <p className="text-xs text-red-500">{error}</p>}
    </div>
  );

  if (q.answerType === "SELECT_MULTIPLE") {
    const selected = new Set((value || "").split(",").filter(Boolean));
    const toggle = (optUuid: string) => {
      const next = new Set(selected);
      if (next.has(optUuid)) next.delete(optUuid);
      else next.add(optUuid);
      onChange(Array.from(next).join(","));
    };
    return wrap(
      <div className="flex flex-col gap-2">
        {q.selectOptions?.map(opt => (
          <label key={opt.uuid} className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" className="rounded" checked={selected.has(opt.uuid)} onChange={() => toggle(opt.uuid)} />
            <span>{opt.title}</span>
            {(opt.description || opt.feeDescription) && (
              <span className="text-xs text-gray-500">
                — {[opt.description, opt.feeDescription].filter(Boolean).join(" · ")}
              </span>
            )}
          </label>
        ))}
      </div>
    );
  }

  if (q.answerType === "SELECT_SINGLE" || q.answerType === "SELECT") {
    const selectedOpt = q.selectOptions?.find(o => o.uuid === value);
    return (
      <div className="flex flex-col gap-1">
        <select className={cls} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur}>
          <option value="">Select an option</option>
          {q.selectOptions?.map(opt => (
            <option key={opt.uuid} value={opt.uuid}>
              {opt.title}
              {opt.feeDescription ? ` — ${opt.feeDescription}` : ""}
            </option>
          ))}
        </select>
        {selectedOpt && (selectedOpt.description || selectedOpt.feeDescription) && (
          <p className="text-xs text-gray-500">
            {[selectedOpt.description, selectedOpt.feeDescription].filter(Boolean).join(" · ")}
          </p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </div>
    );
  }

  if (q.answerType === "BOOLEAN") {
    return wrap(
      <select className={cls} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur}>
        <option value="">Select</option>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }

  if (isLivnResourceQuestion(q)) {
    const resourceUrl = q.resources?.[0]?.url ?? q.example;
    return wrap(
      <div className="flex flex-wrap items-center gap-3">
        {resourceUrl && (
          <a
            href={resourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-primary-color underline hover:opacity-80"
          >
            View Document ↗
          </a>
        )}
        <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
          <input type="checkbox" checked={value === "accepted"} onChange={e => onChange(e.target.checked ? "accepted" : "")} />
          I have read and accept
        </label>
      </div>
    );
  }

  if (isLivnNumericQuestion(q)) {
    const { min, max } = parseLivnNumericBounds(q);
    return wrap(
      <input
        type="number"
        // Bounds are enforced in validateLivnAnswer too — the attributes only stop the
        // native stepper from walking out of range while typing.
        min={min}
        max={max}
        step={q.answerType === "DECIMAL" ? "0.01" : "1"}
        className={cls}
        value={value}
        placeholder={q.example ?? ""}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  if (q.answerType === "EMAIL") {
    return wrap(
      <input
        type="email"
        inputMode="email"
        autoComplete="email"
        className={cls}
        value={value}
        placeholder={q.example ?? "name@example.com"}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  if (q.answerType === "PHONE") {
    return wrap(
      <input
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        className={cls}
        value={value}
        placeholder={q.example ?? "+61 400 000 000"}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
      />
    );
  }

  if (q.answerType === "DATE") {
    return wrap(
      <input type="date" className={cls} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} />
    );
  }

  if (q.answerType === "TIME") {
    return wrap(
      <input type="time" className={cls} value={value} onChange={e => onChange(e.target.value)} onBlur={onBlur} />
    );
  }

  if (q.answerType === "BINARY") {
    const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        onChange("");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // Livn requires the encoding prefix in the payload (e.g. "data:text/plain;base64,...."),
        // so the full data URL is submitted rather than the bare base64 body.
        onChange((reader.result as string) ?? "");
      };
      reader.readAsDataURL(file);
    };
    const encoding = value.startsWith("data:") ? value.slice(5, value.indexOf(",")) : undefined;
    return wrap(
      <div className="flex flex-col gap-1">
        <input type="file" className="text-sm text-gray-600" onChange={handleFile} onBlur={onBlur} />
        {encoding && <p className="text-xs text-green-600">File attached ✓ ({encoding})</p>}
      </div>
    );
  }

  return wrap(
    <input
      type="text"
      className={cls}
      value={value}
      placeholder={q.example ?? ""}
      onChange={e => onChange(e.target.value)}
      onBlur={onBlur}
    />
  );
};

export default LivnQuestionInput;
